<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar" />

    <Sidebar
      class="alice-shell__sidebar"
      variant="rail"
      active-key="changelog"
    />

    <main class="alice-shell__main">
      <PageHeader
        title="Changelog"
        :count="entries.length"
        subtitle="What's new in Alice — features, improvements and fixes."
      />

      <div class="clog-toolbar alice-shell__toolbar">
        <div class="clog-toolbar__search">
          <Icon name="search" :size="16" />
          <input
            v-model="query"
            type="text"
            placeholder="Search the changelog…"
          />
          <span class="clog-toolbar__count"
            >{{ filtered.length }}
            {{ filtered.length === 1 ? "entry" : "entries" }}</span
          >
        </div>
        <div v-if="isMax" class="clog-toolbar__actions">
          <v-btn color="black" @click="openNew">New entry</v-btn>
        </div>
      </div>

      <div class="alice-shell__list">
        <div v-if="loading" class="clog-loading">
          <v-progress-circular indeterminate size="28" width="3" />
        </div>
        <div v-else-if="error" class="clog-empty">
          Couldn't load the changelog.
          <v-btn variant="text" @click="fetchEntries">Retry</v-btn>
        </div>
        <EmptyState v-else-if="filtered.length === 0 && query" :query="query" />
        <div v-else-if="entries.length === 0" class="clog-empty">
          No changelog entries yet.
        </div>

        <section v-for="group in grouped" :key="group.key" class="clog-month">
          <h2 class="clog-month__label">{{ group.label }}</h2>
          <article v-for="e in group.entries" :key="e.id" class="clog-entry">
            <div class="clog-entry__date">{{ shortDate(e.date) }}</div>
            <div class="clog-entry__content">
              <div class="clog-entry__head">
                <span class="clog-tag" :class="`clog-tag--${e.type}`">{{
                  typeLabel(e.type)
                }}</span>
                <h3 class="clog-entry__title">{{ e.title }}</h3>
              </div>
              <p v-if="e.body" class="clog-entry__body">{{ e.body }}</p>
            </div>
            <div v-if="isMax" class="clog-entry__admin">
              <button class="clog-iconbtn" title="Edit" @click="openEdit(e)">
                <Icon name="pencil" :size="16" />
              </button>
              <button
                class="clog-iconbtn"
                title="Delete"
                @click="removeEntry(e)"
              >
                <Icon name="trash" :size="16" />
              </button>
            </div>
          </article>
        </section>
      </div>
    </main>

    <v-dialog v-model="dialog" max-width="520">
      <v-card class="alice-new-dialog">
        <div class="alice-new-dialog__title">
          {{ editing ? "Edit entry" : "New entry" }}
        </div>
        <v-text-field
          v-model="fTitle"
          placeholder="Title"
          variant="outlined"
          autofocus
          hide-details
          class="alice-new-dialog__input"
        />
        <v-textarea
          v-model="fBody"
          placeholder="Short description (1–3 sentences)"
          variant="outlined"
          rows="3"
          hide-details
          class="alice-new-dialog__input"
        />
        <div class="clog-form-row">
          <v-select
            v-model="fType"
            :items="typeOptions"
            item-title="label"
            item-value="value"
            variant="outlined"
            hide-details
            class="alice-new-dialog__input"
          />
          <v-text-field
            v-model="fDate"
            type="date"
            variant="outlined"
            hide-details
            class="alice-new-dialog__input"
          />
        </div>
        <div class="alice-new-dialog__actions">
          <v-btn variant="text" @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="black"
            :loading="saving"
            :disabled="!fTitle.trim()"
            @click="saveEntry"
            >{{ editing ? "Save" : "Add" }}</v-btn
          >
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import PageHeader from "@/components/alice/PageHeader.vue";
import EmptyState from "@/components/alice/EmptyState.vue";
import Icon from "@/components/alice/Icon.vue";

const TYPE_LABELS = { new: "New", improved: "Improved", fixed: "Fixed" };

export default {
  name: "ChangelogPage",
  components: { TopBar, Sidebar, PageHeader, EmptyState, Icon },
  data() {
    return {
      entries: [],
      loading: true,
      error: false,
      query: "",
      dialog: false,
      editing: null,
      fTitle: "",
      fBody: "",
      fType: "new",
      fDate: "",
      saving: false,
      typeOptions: [
        { label: "New", value: "new" },
        { label: "Improved", value: "improved" },
        { label: "Fixed", value: "fixed" },
      ],
    };
  },
  computed: {
    ...mapState({ user: (s) => s.user }),
    isMax() {
      return this.user?.email === "max@braingineers.com";
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.entries;
      return this.entries.filter((e) =>
        [e.title, e.body, e.type, e.date]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
    grouped() {
      const buckets = new Map();
      for (const e of this.filtered) {
        const key = (e.date || "").slice(0, 7);
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(e);
      }
      return [...buckets.entries()]
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([key, entries]) => ({
          key,
          label: this.monthLabel(key),
          entries: [...entries].sort(
            (a, b) =>
              (b.date || "").localeCompare(a.date || "") ||
              (b.createdAt || 0) - (a.createdAt || 0)
          ),
        }));
    },
  },
  created() {
    // Firestore reads require auth; on a fresh load the store user can lag
    // behind route entry, so wait for it instead of fetching into a
    // permission error.
    if (this.user) {
      this.fetchEntries();
    } else {
      const unwatch = this.$watch("user", (u) => {
        if (u) {
          unwatch();
          this.fetchEntries();
        }
      });
    }
  },
  methods: {
    async fetchEntries() {
      this.loading = true;
      this.error = false;
      try {
        const snap = await getDocs(collection(db, "changelog"));
        this.entries = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      } catch (e) {
        console.error("Failed to load changelog:", e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    typeLabel(type) {
      return TYPE_LABELS[type] || type;
    },
    monthLabel(key) {
      const [y, m] = key.split("-").map(Number);
      if (!y || !m) return key;
      return new Date(y, m - 1, 1).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    },
    shortDate(date) {
      if (!date) return "";
      const d = new Date(date + "T00:00:00");
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
    openNew() {
      this.editing = null;
      this.fTitle = "";
      this.fBody = "";
      this.fType = "new";
      this.fDate = new Date().toISOString().slice(0, 10);
      this.dialog = true;
    },
    openEdit(e) {
      this.editing = e;
      this.fTitle = e.title || "";
      this.fBody = e.body || "";
      this.fType = e.type || "new";
      this.fDate = e.date || new Date().toISOString().slice(0, 10);
      this.dialog = true;
    },
    async saveEntry() {
      if (!this.fTitle.trim()) return;
      this.saving = true;
      try {
        const payload = {
          title: this.fTitle.trim(),
          body: this.fBody.trim(),
          type: this.fType,
          date: this.fDate,
          updatedAt: Date.now(),
        };
        if (this.editing) {
          await updateDoc(doc(db, "changelog", this.editing.id), payload);
          const i = this.entries.findIndex((e) => e.id === this.editing.id);
          if (i !== -1) this.entries[i] = { ...this.entries[i], ...payload };
        } else {
          const data = {
            ...payload,
            createdAt: Date.now(),
            createdBy: this.user?.uid || null,
          };
          const ref = await addDoc(collection(db, "changelog"), data);
          this.entries.push({ ...data, id: ref.id });
        }
        this.dialog = false;
      } finally {
        this.saving = false;
      }
    },
    async removeEntry(e) {
      if (!confirm(`Delete "${e.title}"?`)) return;
      await deleteDoc(doc(db, "changelog", e.id));
      this.entries = this.entries.filter((x) => x.id !== e.id);
    },
  },
};
</script>

<style scoped>
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
/* The header scrolls away with the list; only the toolbar stays in view. */
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
}
.alice-shell__main > * {
  flex-shrink: 0;
}
.alice-shell__toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
}
.alice-shell__list {
  flex-grow: 1;
  background: var(--vp-white);
}

.clog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 32px;
  background: var(--vp-white);
  border-bottom: 1px solid var(--border-hairline);
}
.clog-toolbar__search {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  color: var(--fg-3);
}
.clog-toolbar__search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-1);
}
.clog-toolbar__count {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--fg-3);
  white-space: nowrap;
}
.clog-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clog-empty {
  padding: 80px 40px;
  text-align: center;
  font-family: var(--font-body);
  font-size: 18px;
  color: var(--fg-2);
}
.clog-loading {
  display: flex;
  justify-content: center;
  padding: 80px 40px;
  color: var(--fg-3);
}

.clog-month {
  padding: 24px 32px 8px;
}
.clog-month__label {
  margin: 0 0 12px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.clog-entry {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.clog-entry__date {
  width: 56px;
  flex-shrink: 0;
  padding-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--fg-3);
}
.clog-entry__content {
  flex: 1;
  min-width: 0;
}
.clog-entry__head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.clog-entry__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  color: var(--vp-black);
}
.clog-entry__body {
  margin: 4px 0 0;
  font-family: var(--font-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--fg-2);
}
.clog-entry__admin {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.clog-entry:hover .clog-entry__admin {
  opacity: 1;
}
.clog-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--fg-3);
  cursor: pointer;
}
.clog-iconbtn:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}

.clog-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
}
.clog-tag--new {
  color: var(--vp-turquoise-160);
  background: var(--vp-turquoise-20);
}
.clog-tag--improved {
  color: var(--vp-geel-160);
  background: var(--vp-geel-20);
}
.clog-tag--fixed {
  color: var(--vp-grijs-160);
  background: var(--vp-grijs-20);
}

.clog-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.alice-new-dialog {
  padding: 24px;
  border-radius: 0 !important;
  font-family: var(--font-body);
}
.alice-new-dialog__title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--vp-black);
}
.alice-new-dialog__input {
  margin-bottom: 16px;
}
.alice-new-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
